import { useEffect, useState } from 'react';
import type { UserMembership } from '@vtak/shared';
import { ChevronRight, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { switchWorkspace } from '@/lib/workspace-switch';
import { resolveEmblem } from '@/constants/emblems';
import vtakLogo from '@/assets/vtak-logo.png';

/**
 * Full-screen unit picker shown when a user has more than one approved unit.
 * Keyboard-navigable (↑/↓/Enter); selecting a unit switches the workspace.
 */
export function UnitSelector({ memberships }: { memberships: UserMembership[] }): React.JSX.Element {
  const persisted = useWorkspaceStore((s) => s.selectedUnitId);
  const [highlight, setHighlight] = useState(() => initialIndex(memberships, persisted));

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((i) => Math.min(i + 1, memberships.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const chosen = memberships[highlight];
        if (chosen) switchWorkspace(chosen.unitId);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [memberships, highlight]);

  return (
    <div className="fixed inset-0 z-[2000] grid place-items-center bg-[#0d0d0d] p-4">
      <div className="w-full max-w-md rounded-xl border border-echo-border bg-[#1a1a1a] p-6 shadow-2xl">
        <div className="mb-5 flex flex-col items-center gap-3">
          <img src={vtakLogo} alt="VTAK" className="h-11 w-auto" />
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Select a unit</p>
        </div>

        <ul className="flex flex-col gap-1.5">
          {memberships.map((m, i) => (
            <li key={m.unitId}>
              <UnitRow
                membership={m}
                highlighted={i === highlight}
                onHover={() => setHighlight(i)}
                onSelect={() => switchWorkspace(m.unitId)}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** A single selectable unit row */
function UnitRow({
  membership,
  highlighted,
  onHover,
  onSelect,
}: {
  membership: UserMembership;
  highlighted: boolean;
  onHover: () => void;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <button
      onMouseEnter={onHover}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors focus-visible:outline-none',
        highlighted
          ? 'border-echo-yellow/40 bg-echo-yellow/10'
          : 'border-echo-border bg-transparent hover:bg-white/[0.04]',
      )}
    >
      <RowEmblem emblemUrl={resolveEmblem(membership.emblem)} shortName={membership.shortName} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-foreground">{membership.shortName}</p>
        <p className="truncate text-xs text-muted-foreground">{membership.unitName}</p>
      </div>
      {membership.role === 'admin' && (
        <span className="inline-flex items-center gap-1 rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          <ShieldCheck className="h-3 w-3" />
          admin
        </span>
      )}
      <ChevronRight className={cn('h-4 w-4 shrink-0', highlighted ? 'text-echo-yellow' : 'text-muted-foreground')} />
    </button>
  );
}

/** Unit patch (or shield fallback) shown at the left of a selector row. */
function RowEmblem({ emblemUrl, shortName }: { emblemUrl: string | null; shortName: string }): React.JSX.Element {
  if (emblemUrl) {
    return (
      <img
        src={emblemUrl}
        alt={`${shortName} emblem`}
        className="h-8 w-8 shrink-0 rounded-md object-contain ring-1 ring-echo-border"
      />
    );
  }
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-echo-border/40">
      <ShieldCheck className="h-4 w-4 text-echo-yellow" />
    </span>
  );
}

/** Index of the persisted unit, or 0 */
function initialIndex(memberships: UserMembership[], persisted: string | null): number {
  const idx = memberships.findIndex((m) => m.unitId === persisted);
  return idx >= 0 ? idx : 0;
}
